import { Alert, AlertTitle, Box, Button, capitalize, FormControl, Snackbar, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isErrorResponse } from '@ts-rest/core';
import { isFetchError, isUnknownErrorResponse } from '@ts-rest/react-query/v5';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useState } from 'react';
import { DATEPICKER_FORMAT, PAGE_CONTENT_MAX_WIDTH_MD } from '~/constants';
import { useSnackbar } from '~/hooks/useSnackbar';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';

const logger = new Logger('Report Form Dispatched Products');

export const ReportFormDispatchedProducts: FC = () => {
  const { warehouseApi } = useApi();
  const { snackbar, updateSnackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);

  const handleStartDateChange = (date: DateTime | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: DateTime | null) => {
    setEndDate(date);
  };

  const handleCloseSnackbar = () => {
    closeSnackbar();
    setTimeout(() => updateSnackbar(''), 500); // Delay to allow the snackbar to close
  };

  const { mutateAsync, isPending, contractEndpoint } = warehouseApi.exportDispatchedProductsReport.useMutation({
    onSuccess: () => {
      showSnackbar('Dispatched products report generated and sent to your email.', 'success');
    }
  });

  const handleGenerateReport = async () => {
    const startDateIso = startDate && startDate.toUTC().toISO();
    const endDateIso = endDate && endDate.toUTC().toISO();

    if (!startDateIso || !endDateIso) {
      showSnackbar('Please select both start and end dates', 'error');
      return;
    }

    try {
      await mutateAsync({
        body: {
          data: {
            startDate: startDateIso,
            endDate: endDateIso
          }
        }
      });
    } catch (error) {
      const snackbarMessage = 'Failed to generate dispatched products report';

      if (isFetchError(error)) {
        showSnackbar(snackbarMessage, 'error');
        logger.error('Fetch error when attempting to generate dispatched product report', error);
        return;
      }

      if (isUnknownErrorResponse(error, contractEndpoint)) {
        showSnackbar(snackbarMessage, 'error');
        logger.error('Unknown error when attempting to generate dispatched product report', error);
        return;
      }

      if (isErrorResponse(error) && error.status === 400 && error.body.error.message) {
        showSnackbar(error.body.error.message, 'error');
        logger.error(error.body.error.message, error);
        return;
      }

      // Fallback error handling
      showSnackbar(snackbarMessage, 'error');
      logger.error('Error when attempting to generate dispatched product report', error);
      return;
    }
  };

  const isDisabled = !startDate || !endDate || isPending;

  return (
    <>
      <Snackbar
        open={snackbar.isOpen && snackbar.message !== ''}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.type && <AlertTitle>{capitalize(snackbar.type)}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box component="section">
        <Stack maxWidth={PAGE_CONTENT_MAX_WIDTH_MD} mx="auto" px={{ xs: 3, lg: 0 }} spacing={4} alignItems="center">
          {/* Date Range filtering */}
          <FormControl sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={DateTime.now()}
                format={DATEPICKER_FORMAT}
                slotProps={{
                  field: { clearable: true },
                  openPickerButton: { id: 'dispatch_order_report_start_datepicker_open_calendar_button' },
                  clearButton: { id: 'dispatch_order_report_start_datepicker_clear_button' }
                }}
                shouldDisableDate={(date) => !!(endDate && date > endDate)}
                sx={{ flex: 1 }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                maxDate={DateTime.now()}
                format={DATEPICKER_FORMAT}
                slotProps={{
                  field: { clearable: true },
                  openPickerButton: { id: 'dispatch_order_report_end_datepicker_open_calendar_button' },
                  clearButton: { id: 'dispatch_order_report_end_datepicker_clear_button' }
                }}
                shouldDisableDate={(date) => !!(startDate && date < startDate)}
                sx={{ flex: 1 }}
              />
            </Stack>
          </FormControl>

          {/* Export */}
          <Button
            id="dispatch_order_report_generate_report_button"
            size="large"
            variant="contained"
            color="primary"
            disabled={isDisabled}
            onClick={handleGenerateReport}
          >
            {isPending ? 'Generating...' : 'Generate Report'}
          </Button>
        </Stack>
      </Box>
    </>
  );
};
